// Default config passed to i18n init (can be overridden)
export const defaultI18nConfig = {
    fallbackLng: {
        ca: ['ca-ES', 'ca', 'en'],
        fr: ['fr-FR', 'en'],
        'ja-JP': ['ja', 'en'],
        'es-*': ['es', 'en'],
        'en-GB-*': ['en-GB', 'en'],
        default: ['en'],
    },
    detection: {
        // An empty array is passed to prevent default localStorage caching
        caches: [],
        lookupQuerystring: 'lang_code',
        order: ['htmlTag', 'querystring', 'navigator'],
    },
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    react: {
        useSuspense: true,
    },
};
/**
 * Resolves a locale string to its fallback based on i18next fallbackLng config.
 *
 * Used for `?lang_code` overrides, standalone hint resolution, and i18next key
 * resolution. Accept-Language negotiation is handled separately by the
 * family-first algorithm in `detectLanguageFromRequest`.
 *
 * @example
 * resolveFallbackLocale('ja-JP', validLocales) // 'ja-JP' (exact match)
 * resolveFallbackLocale('ja-TEST', validLocales) // 'ja' (fallback)
 * resolveFallbackLocale('ca', validLocales)      // 'ca-ES'
 * resolveFallbackLocale('es-MX', validLocales)   // 'es'
 */
export function resolveFallbackLocale(locale, validLocales) {
    if (!locale) {
        return null;
    }
    // BCP-47 tags are case-insensitive (RFC 5646). Build a lowercase→canonical
    // map so lookups work regardless of input casing (e.g., 'en-us' → 'en-US').
    const localeMap = new Map([...validLocales].map((l) => [l.toLowerCase(), l]));
    const input = locale.toLowerCase();
    // Exact match first — if the locale is directly in VALID_LOCALES, use it.
    // This prevents ja-JP (a valid locale) from being collapsed to ja via fallback.
    const exactMatch = localeMap.get(input);
    if (exactMatch) {
        return exactMatch;
    }
    const fallbackConfig = defaultI18nConfig.fallbackLng;
    const findValid = (fallbacks) => fallbacks?.find((fb) => validLocales.has(fb));
    const fallbackKeys = Object.keys(fallbackConfig);
    // Explicit fallback (e.g., 'ja-JP' -> ['ja'] — only reached if ja-JP not in validLocales)
    const matchedKey = fallbackKeys.find((k) => k.toLowerCase() === input);
    const explicit = findValid(matchedKey ? fallbackConfig[matchedKey] : undefined);
    if (explicit) {
        return explicit;
    }
    // Wildcard fallback — check most-specific to least-specific prefixes
    // (e.g., 'en-GB-oed' checks 'en-GB-*' then 'en-*')
    const parts = input.split('-');
    for (let i = parts.length - 1; i >= 1; i--) {
        const prefix = parts.slice(0, i).join('-');
        const wildcardKey = fallbackKeys.find((k) => k.toLowerCase() === `${prefix}-*`);
        const wildcard = findValid(wildcardKey ? fallbackConfig[wildcardKey] : undefined);
        if (wildcard) {
            return wildcard;
        }
    }
    // Base locale fallback rules (e.g., 'ca-FAKE' -> 'ca' -> ['ca-ES', 'ca', 'en'] -> 'ca-ES')
    const baseLocale = parts[0];
    const baseKey = fallbackKeys.find((k) => k.toLowerCase() === baseLocale);
    const baseFallback = findValid(baseKey ? fallbackConfig[baseKey] : undefined);
    if (baseFallback) {
        return baseFallback;
    }
    // Base locale exact match
    return localeMap.get(baseLocale) ?? null;
}
